import { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface CanAccessProps {
  /** Nome da permissão ou array de permissões necessárias (lógica OR - precisa de pelo menos uma) */
  permission?: string | string[];

  /** Array de permissões onde o usuário precisa ter TODAS (lógica AND) */
  allPermissions?: string[];

  /** Se true, verifica isAdmin como fallback (para compatibilidade temporária) */
  allowAdmin?: boolean;

  /** Conteúdo a ser renderizado se o usuário tiver permissão */
  children: ReactNode;

  /** Conteúdo alternativo a ser renderizado se o usuário NÃO tiver permissão */
  fallback?: ReactNode;
}

/**
 * Componente para controle de acesso baseado em permissões
 *
 * @example
 * // Renderiza apenas se tiver permissão users:create
 * <CanAccess permission="users:create">
 *   <button>Criar Usuário</button>
 * </CanAccess>
 *
 * @example
 * // Renderiza se tiver qualquer uma das permissões (OR)
 * <CanAccess permission={["users:create", "users:update"]}>
 *   <button>Gerenciar Usuários</button>
 * </CanAccess>
 *
 * @example
 * // Renderiza apenas se tiver TODAS as permissões (AND)
 * <CanAccess allPermissions={["users:create", "users:delete"]}>
 *   <button>Gerenciar Usuários Completo</button>
 * </CanAccess>
 *
 * @example
 * // Com fallback para admin
 * <CanAccess permission="dashboard:view" allowAdmin>
 *   <Dashboard />
 * </CanAccess>
 *
 * @example
 * // Com conteúdo alternativo
 * <CanAccess permission="users:read" fallback={<p>Sem permissão</p>}>
 *   <UsersList />
 * </CanAccess>
 */
export function CanAccess({
  permission,
  allPermissions,
  allowAdmin = false,
  children,
  fallback = null,
}: CanAccessProps) {
  const { user, hasPermission, hasAllPermissions, permissionsLoading } = useAuth();

  // Enquanto carrega, não renderiza nada (ou pode renderizar um loading)
  if (permissionsLoading) {
    return null;
  }

  // Se não tem usuário, não renderiza
  if (!user) {
    return <>{fallback}</>;
  }

  // Se allowAdmin está ativo e o usuário é admin, permite acesso
  if (allowAdmin && user.isAdmin) {
    return <>{children}</>;
  }

  // Verifica se precisa ter TODAS as permissões (AND)
  if (allPermissions && allPermissions.length > 0) {
    const hasAll = hasAllPermissions(allPermissions);
    return hasAll ? <>{children}</> : <>{fallback}</>;
  }

  // Verifica se precisa ter pelo menos UMA permissão (OR)
  if (permission) {
    const has = hasPermission(permission);
    return has ? <>{children}</> : <>{fallback}</>;
  }

  // Se não especificou nenhuma permissão, renderiza por padrão
  return <>{children}</>;
}
